import {
  Badge,
  Button,
  Card,
  Group,
  SegmentedControl,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { api, unwrap } from '../api.ts';
import { bookingStatusColor, bookingTypeLabel, dayWindow, timeRange } from '../format.ts';
import { Empty, Failure, Loading, Page, useSave } from '../ui.tsx';

const STATUSES = ['all', 'approved', 'pending', 'cancelled'] as const;

export function BookingsPage() {
  const search = useSearch({ from: '/bookings' });
  const navigate = useNavigate({ from: '/bookings' });
  const setSearch = (next: Partial<typeof search>) =>
    void navigate({ search: (previous) => ({ ...previous, ...next }), replace: true });
  const buildings = useQuery({ queryKey: ['buildings'], queryFn: () => unwrap(api().GET('/v1/buildings')) });
  const buildingId = search.buildingId ?? buildings.data?.[0]?.id;
  const building = buildings.data?.find((candidate) => candidate.id === buildingId);
  const day = search.day ?? new Date().toISOString().slice(0, 10);
  const status = search.status ?? 'all';
  const window = dayWindow(day, building?.timeZone);

  const bookings = useQuery({
    queryKey: ['bookings', buildingId, day],
    enabled: Boolean(buildingId),
    queryFn: () =>
      unwrap(
        api().GET('/v1/booked-resources', {
          params: { query: { ...window, ...(buildingId ? { buildingId } : {}), mine: false } },
        }),
      ),
  });
  const spaces = useQuery({
    queryKey: ['spaces', buildingId],
    enabled: Boolean(buildingId),
    queryFn: () =>
      unwrap(
        api().GET('/v1/buildings/{buildingId}/spaces', {
          params: { path: { buildingId: buildingId ?? '' } },
        }),
      ),
  });
  const users = useQuery({ queryKey: ['users'], queryFn: () => unwrap(api().GET('/v1/users')) });
  const cancel = useSave({
    run: (bookedResourceId: string) =>
      unwrap(
        api().POST('/v1/booked-resources/{bookedResourceId}/cancel', {
          params: { path: { bookedResourceId } },
        }),
      ),
    invalidates: [['bookings']],
    success: 'Booking cancelled; the organizer is notified',
  });

  const spaceName = (id: string) => spaces.data?.find((space) => space.id === id)?.name ?? '…';
  const organizer = (id: string | null) => {
    if (!id) return 'Calendar';
    const user = users.data?.find((candidate) => candidate.id === id);
    return user?.displayName ?? user?.email ?? 'Someone from another organization';
  };
  const shown = (bookings.data ?? [])
    .filter((booking) => status === 'all' || booking.status === status)
    .toSorted((a, b) => a.start.localeCompare(b.start));

  return (
    <Page title="Bookings" description="Every booking in a building on one day, across your organization.">
      <Card withBorder padding="lg">
        <Stack>
          <Group wrap="wrap">
            <Select
              label="Building"
              data={
                buildings.data?.map((candidate) => ({ value: candidate.id, label: candidate.name })) ?? []
              }
              value={buildingId ?? null}
              onChange={(value) => value && setSearch({ buildingId: value })}
              allowDeselect={false}
              w={240}
            />
            <TextInput
              label="Day"
              type="date"
              value={day}
              onChange={(event) => setSearch({ day: event.currentTarget.value || undefined })}
              w={180}
            />
            <div>
              <Text size="sm" fw={500} mb={4}>
                Status
              </Text>
              <SegmentedControl
                value={status}
                onChange={(value) => setSearch({ status: value === 'all' ? undefined : value })}
                data={STATUSES.map((value) => ({
                  value,
                  label: value === 'all' ? 'All' : value[0]?.toUpperCase() + value.slice(1),
                }))}
              />
            </div>
          </Group>
          {bookings.isPending && buildingId ? <Loading /> : null}
          <Failure error={bookings.error ?? buildings.error} />
          {bookings.data && shown.length === 0 ? <Empty>No bookings on this day.</Empty> : null}
          {shown.length > 0 && building ? (
            <Table.ScrollContainer minWidth={760}>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>When</Table.Th>
                    <Table.Th>Place</Table.Th>
                    <Table.Th>Type</Table.Th>
                    <Table.Th>Title</Table.Th>
                    <Table.Th>Organizer</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {shown.map((booking) => (
                    <Table.Tr key={booking.id}>
                      <Table.Td>{timeRange(booking.start, booking.end, building.timeZone)}</Table.Td>
                      <Table.Td>{spaceName(booking.spaceId)}</Table.Td>
                      <Table.Td>{bookingTypeLabel(booking.bookingType)}</Table.Td>
                      <Table.Td>{booking.title ?? '—'}</Table.Td>
                      <Table.Td>{organizer(booking.organizerUserId)}</Table.Td>
                      <Table.Td>
                        <Badge variant="light" color={bookingStatusColor(booking.status)}>
                          {booking.status.replace('_', ' ')}
                        </Badge>
                      </Table.Td>
                      <Table.Td ta="right">
                        {booking.status === 'approved' || booking.status === 'pending' ? (
                          <Button
                            size="xs"
                            variant="light"
                            color="red"
                            onClick={() =>
                              modals.openConfirmModal({
                                title: 'Cancel this booking?',
                                children: (
                                  <Text size="sm">
                                    {spaceName(booking.spaceId)},{' '}
                                    {timeRange(booking.start, booking.end, building.timeZone)}. The place
                                    becomes free and the organizer is told.
                                  </Text>
                                ),
                                labels: { confirm: 'Cancel booking', cancel: 'Keep it' },
                                confirmProps: { color: 'red' },
                                onConfirm: () => cancel.mutate(booking.id),
                              })
                            }
                          >
                            Cancel
                          </Button>
                        ) : null}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          ) : null}
        </Stack>
      </Card>
    </Page>
  );
}
